import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

interface AccessibilityStatementSection {
  title: string;
  body: string;
  points?: string[];
}

interface AccessibilityStatementAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function AccessibilityStatementPage() {
  const { t } = useTranslation('marketing');
  const sections = t('pages.accessibilityStatement.sections', { returnObjects: true }) as AccessibilityStatementSection[];
  const sidebarPoints = t('pages.accessibilityStatement.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.accessibilityStatement.cta.actions', { returnObjects: true }) as AccessibilityStatementAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.accessibilityStatement.meta.title')}
      metaDescription={t('pages.accessibilityStatement.meta.description')}
      canonicalPath="/accessibility-statement"
      eyebrow={t('pages.accessibilityStatement.hero.eyebrow')}
      title={t('pages.accessibilityStatement.hero.title')}
      intro={t('pages.accessibilityStatement.hero.intro')}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.accessibilityStatement.sidebar.title')}</h2>
            <p>{t('pages.accessibilityStatement.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.accessibilityStatement.feedback.title')}</h2>
            <p>{t('pages.accessibilityStatement.feedback.body')}</p>
            <div className="acc-public-page-actions">
              <PublicCtaLink href="/contact" variant="secondary">
                {t('pages.accessibilityStatement.feedback.action')}
              </PublicCtaLink>
            </div>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.accessibilityStatement.intro.title')}</h2>
        <p>{t('pages.accessibilityStatement.intro.body')}</p>
        <p>{t('pages.accessibilityStatement.intro.workingStatement')}</p>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.accessibilityStatement.disclaimer.title')}</h2>
        <p>{t('pages.accessibilityStatement.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="accessibility-statement-cta-title">
        <div>
          <h2 id="accessibility-statement-cta-title">{t('pages.accessibilityStatement.cta.title')}</h2>
          <p>{t('pages.accessibilityStatement.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
